/**
 * Provider-agnostic event tracking + Supabase live event recording.
 *
 * - Pushes to window.dataLayer (GTM / Plausible / Segment compatible)
 * - Persists every event to public.seo_events for the SEO Growth Engine dashboard
 *   (live activity feed, funnel, traffic counts, real-time visitors)
 *
 * Designed to be a no-op when no analytics layer or DB are reachable.
 * NEVER throws — tracking failures must never break the user-facing UI.
 */
import { supabase } from "@/integrations/supabase/client";
import { getFunnelState } from "@/lib/conversionFunnel";

type TrackPayload = Record<string, string | number | boolean | undefined | null>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const SESSION_KEY = "nardy_session_id";

/** Persistent per-tab session id (sessionStorage so it dies with the tab). */
const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        crypto && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `s_${Date.now()}`;
  }
};

/** Best-effort slug from current URL. */
const currentSlug = (): string => {
  if (typeof window === "undefined") return "";
  return window.location.pathname.replace(/^\/+/, "").split("?")[0] || "home";
};

/** Best-effort page metadata classifier from slug. Pure heuristics — never throws. */
const classifySlug = (slug: string): { page_type: string | null; brand: string | null; topic_cluster: string | null } => {
  const s = slug.toLowerCase();
  let page_type: string | null = null;
  if (/-fault-codes$|-error-codes$/.test(s)) page_type = "error_codes";
  else if (/-replacement-options$|-replacement$/.test(s)) page_type = "replacement";
  else if (/^buy-/.test(s)) page_type = "buy_intent";
  else if (/-vs-/.test(s)) page_type = "comparison";
  else if (/-for-/.test(s)) page_type = "application";
  else if (/-industrial$/.test(s)) page_type = "category_expansion";

  const brands = [
    "allen-bradley", "powerflex", "controllogix", "compactlogix",
    "siemens", "sinamics", "simatic", "sitop",
    "abb", "schneider", "altivar", "mitsubishi", "fanuc",
    "yaskawa", "omron", "sew", "movidrive", "bosch-rexroth", "rexroth",
    "lenze", "delta", "nidec", "beckhoff",
  ];
  const brand = brands.find((b) => s.includes(b)) || null;

  let topic_cluster: string | null = null;
  if (/fault|error|troubleshoot/.test(s)) topic_cluster = "troubleshooting";
  else if (/replacement|equivalent|alternative/.test(s)) topic_cluster = "replacement";
  else if (/buy|stock|distributor|supplier/.test(s)) topic_cluster = "buy_intent";
  else if (/comparison|vs/.test(s)) topic_cluster = "comparison";

  return { page_type, brand, topic_cluster };
};

/** Allowed event names. */
type SeoEventName =
  | "page_view"
  | "quote_click"
  | "form_submit"
  | "cta_click"
  | "scroll_deep"
  | "outbound_click"
  | "site_search"
  | "result_click"
  | "zero_result_search"
  | "add_to_cart"
  | "checkout_start"
  | "purchase"
  | "download"
  | "whatsapp_click"
  | "phone_click"
  | "rfq_click"
  | "funnel_progress";

const VALID_EVENTS: ReadonlySet<string> = new Set<string>([
  "page_view",
  "quote_click",
  "form_submit",
  "cta_click",
  "scroll_deep",
  "outbound_click",
  "site_search",
  "result_click",
  "zero_result_search",
  "add_to_cart",
  "checkout_start",
  "purchase",
  "download",
  "whatsapp_click",
  "phone_click",
  "rfq_click",
  "funnel_progress",
]);

/** Fire-and-forget DB insert via edge function (which captures country from headers).
 *  Falls back to direct insert (no country) if the function call fails. */
const persistEvent = (event: SeoEventName, payload: TrackPayload) => {
  if (typeof window === "undefined") return;
  const slug = (payload.page_slug as string) || currentSlug();
  const meta = classifySlug(slug);
  const row = {
    event_name: event,
    page_slug: slug,
    page_type: ((payload.page_type as string) || meta.page_type) ?? undefined,
    brand: ((payload.brand as string) || meta.brand) ?? undefined,
    topic_cluster: ((payload.topic_cluster as string) || meta.topic_cluster) ?? undefined,
    source: (payload.source as string) || "organic",
    session_id: getSessionId(),
    referrer: document.referrer ? document.referrer.slice(0, 500) : undefined,
    metadata: payload as unknown as Record<string, never>,
    // Search-specific fields lifted out of payload so the edge function can store them in dedicated columns
    search_query: (payload.search_query as string) || undefined,
    result_count: typeof payload.result_count === "number" ? payload.result_count : undefined,
    clicked_result_slug: (payload.clicked_result_slug as string) || undefined,
  };

  // Primary path: edge function so server-side country header is captured.
  void supabase.functions.invoke("track-seo-event", { body: row }).then(({ error }) => {
    if (!error) return;
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn("[trackEvent] edge function failed, falling back to direct insert:", error.message);
    }
    // Fallback: direct insert (no country) so we never lose the event.
    void supabase.from("seo_events").insert([{ ...row, user_agent: navigator.userAgent.slice(0, 500) }]).then(({ error: e2 }) => {
      if (e2 && import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn("[trackEvent] fallback insert failed:", e2.message);
      }
    });
  });
};

export const trackEvent = (event: string, payload: TrackPayload = {}) => {
  if (typeof window === "undefined") return;

  // Auto-attribution: enrich every event with visitor context so conversion
  // attribution is consistent across CTAs without every callsite having to
  // remember every field. Explicit payload values always win.
  try {
    const funnel = getFunnelState();
    const enriched: TrackPayload = {
      page_path: window.location.pathname,
      visitor_stage: funnel.stage,
      visitor_score: funnel.score,
      session_id: getSessionId(),
      ...payload,
    };
    payload = enriched;
  } catch { /* never throw from tracking */ }

  // Duplicate-event guard: identical event+key fired within 800ms is dropped.
  // Prevents double-tap taps, React strict-mode double effects, and
  // component-remount echoes from inflating counts.
  try {
    const w = window as unknown as { __nardyEventDedupe?: Map<string, number> };
    if (!w.__nardyEventDedupe) w.__nardyEventDedupe = new Map();
    const dedupeKey = `${event}|${(payload as any).page_slug || ""}|${(payload as any).product_slug || ""}|${(payload as any).search_query || ""}|${(payload as any).source || ""}`;
    const now = Date.now();
    const prev = w.__nardyEventDedupe.get(dedupeKey) || 0;
    if (now - prev < 800) return;
    w.__nardyEventDedupe.set(dedupeKey, now);
    // GC old entries
    if (w.__nardyEventDedupe.size > 200) {
      for (const [k, t] of w.__nardyEventDedupe) if (now - t > 60_000) w.__nardyEventDedupe.delete(k);
    }
  } catch { /* ignore */ }

  // 1. Always push to dataLayer (GTM / Plausible)
  if (!Array.isArray(window.dataLayer)) window.dataLayer = [];
  window.dataLayer.push({ event, ...payload, ts: Date.now() });

  // 2. Persist allowed events to seo_events for the dashboard
  if (VALID_EVENTS.has(event)) {
    persistEvent(event as SeoEventName, payload);
  }
};

/** Convenience hook for components. */
export const useTrackEvent = () => trackEvent;

export { getSessionId };
